"use client";

// Stores the number on the served design's header cart for the signed-in member, so another site's header (Accounts) can show the same number.
// Wiring only; the rules (what, when, never a false zero) are in `@/lib/cart-badge-publisher`, the count is `@/lib/cart-badge`.
//
// The trigger is the design's own `sg:cartquotechange`, raised right after it persists `sg-cart-v1` on every real cart change (SG-CART-PERSIST-023);
// plus a verified sign-in, the storage event of another tab, and focus. Only a server-verified cookie session publishes. Renders nothing.
import { useEffect, useRef } from "react";

import { isSignedIn, useCartStore } from "@/lib/cart-store";
import { designCartBadge, DESIGN_CART_KEY } from "@/lib/cart-badge";
import { createCartBadgePublisher } from "@/lib/cart-badge-publisher";
import type { CartBadgePublisher as Publisher } from "@/lib/cart-badge-publisher";

export default function CartBadgePublisher() {
  const session = useCartStore((s) => s.session);
  // a verified identity is the server-proven cookie session; a legacy bearer session cannot be verified server-side, so it is not one
  const who = isSignedIn(session) && session.cookie === true ? `sso:${session.email ?? ""}` : null;
  const publisher = useRef<Publisher | null>(null);

  useEffect(() => {
    const p = createCartBadgePublisher({
      readRaw: () => {
        try {
          return window.localStorage.getItem(DESIGN_CART_KEY);
        } catch {
          return null;
        }
      },
      countOf: designCartBadge,
      hydrated: () => useCartStore.persist.hasHydrated(),
      // the host's merged-cart handover (window.__SG_CART_SYNC) is deleted by the design once applied
      handoverPending: () => (window as unknown as { __SG_CART_SYNC?: unknown }).__SG_CART_SYNC !== undefined,
      send: async (count) => {
        try {
          const res = await fetch("/api/cart/count", { method: "PUT", credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(10_000), headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ count }) });
          return res.ok;
        } catch {
          return false;
        }
      },
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (t) => window.clearTimeout(t as number),
    });
    publisher.current = p;
    const poke = () => p.poke();
    const onStorage = (e: StorageEvent) => {
      if (e.key === DESIGN_CART_KEY || e.key === null) p.poke();
    };
    window.addEventListener("sg:cartquotechange", poke);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", poke);
    const unsub = useCartStore.persist.onFinishHydration(poke);
    return () => {
      window.removeEventListener("sg:cartquotechange", poke);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", poke);
      unsub();
      p.dispose();
      publisher.current = null;
    };
  }, []);

  useEffect(() => {
    publisher.current?.setIdentity(who);
  }, [who]);

  return null;
}
